import { useState } from 'react';
import { logEvent } from '@/config/analytics';
import { appConfig, buildRoomLink } from '@/config/app-config';
import { getConsent } from '@/config/consent';
import { looksLikeRoomLink, parseRoomInput } from '@/call/room';
import type { Translator } from '@/i18n';
import { ConsentBanner } from './ConsentBanner';
import { HeroAction } from './HeroAction';
import { Icon } from './Icon';

interface Props {
  t: Translator['t'];
  onCreateRoom: () => void;
  onJoinRoom: (roomId: string) => void;
  onScheduleCall: () => { roomId: string; outcome: 'opened' | 'blocked' };
  errorText: string | null;
  onDismissError: () => void;
}

/**
 * Стартовий екран.
 *
 * Повторює велком-діалог Flutter-клієнта: градієнтна рамка навколо темної
 * картки, головна градієнтна дія вгорі, речення про суть дзвінка за
 * посиланням, поле входу, що приймає і посилання, і ID, пігулка планування,
 * кнопки магазинів і юридичні посилання. Гість за посиланням сюди не
 * потрапляє — у нього `GuestScreen`.
 */
export function StartScreen({
  t,
  onCreateRoom,
  onJoinRoom,
  onScheduleCall,
  errorText,
  onDismissError,
}: Props): React.JSX.Element {
  const [input, setInput] = useState('');
  const [touched, setTouched] = useState(false);
  const [scheduleNotice, setScheduleNotice] = useState<string | null>(null);
  const [consentOpen, setConsentOpen] = useState(() => getConsent() === null);

  const roomId = parseRoomInput(input);
  const invalid = touched && input.trim().length > 0 && roomId === null;
  const canJoin = roomId !== null;

  const join = (): void => {
    if (roomId === null) return;
    logEvent('room_joined', { via: looksLikeRoomLink(input) ? 'typed_link' : 'typed_id' });
    onJoinRoom(roomId);
  };

  const schedule = (): void => {
    const { roomId: scheduled, outcome } = onScheduleCall();
    logEvent('schedule_call', { outcome });
    if (outcome === 'opened') {
      setScheduleNotice(t('calendarOpened'));
      return;
    }
    // Браузер заблокував нове вікно — віддаємо посилання, а не лишаємо
    // користувача ні з чим.
    const link = buildRoomLink(scheduled);
    void navigator.clipboard
      .writeText(link)
      .then(() => {
        setScheduleNotice(t('linkCopiedToClipboard'));
      })
      .catch(() => {
        setScheduleNotice(link);
      });
  };

  return (
    <main className={consentOpen ? 'start start--with-consent' : 'start'}>
      <div className="dialog">
        <div className="dialog__inner">
          <h1 className="dialog__title dialog__title--tight">{t('readyToConnect')}</h1>
          <p className="dialog__explainer">{t('welcomeExplainer')}</p>

          {errorText !== null && (
            <button type="button" className="banner" onClick={onDismissError}>
              {errorText}
            </button>
          )}

          <div className="actions">
            <HeroAction
              icon="videocam"
              title={t('startOneOnOne')}
              subtitle={t('createPrivateLink')}
              onClick={onCreateRoom}
            />
          </div>

          <hr className="divider" />

          <p className="section-label">{t('joinHaveLink')}</p>

          <div className="join">
            <input
              className="join__input"
              value={input}
              placeholder={t('joinFieldHint')}
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              aria-label={t('joinFieldHint')}
              onChange={(event) => {
                setInput(event.target.value);
                setTouched(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canJoin) join();
              }}
            />
            <button
              type="button"
              className="join__button"
              disabled={!canJoin}
              onClick={() => {
                setTouched(true);
                join();
              }}
            >
              {t('joinRoom')}
            </button>
          </div>

          {invalid && <p className="field-error">{t('roomInputInvalid')}</p>}

          <div className="centered">
            <button type="button" className="pill" onClick={schedule}>
              <Icon name="calendar_today" size={16} />
              {t('scheduleCall')}
            </button>
          </div>
          {scheduleNotice !== null && <p className="hint">{scheduleNotice}</p>}

          <hr className="divider divider--faint" />

          <div className="stores">
            <a className="store" href={appConfig.appStoreUrl} target="_blank" rel="noreferrer">
              <Icon name="phone_iphone" size={18} />
              App Store
            </a>
            <a className="store" href={appConfig.playStoreUrl} target="_blank" rel="noreferrer">
              <Icon name="android" size={18} />
              Google Play
            </a>
          </div>

          <nav className="legal">
            <a href={appConfig.termsOfUseUrl} target="_blank" rel="noreferrer">
              {t('termsOfUse')}
            </a>
            <span>|</span>
            <a href={appConfig.privacyPolicyUrl} target="_blank" rel="noreferrer">
              {t('privacyPolicy')}
            </a>
            <span>|</span>
            <a href={appConfig.supportUrl} target="_blank" rel="noreferrer">
              {t('howToUse')}
            </a>
            <span>|</span>
            <button
              type="button"
              className="legal__button"
              onClick={() => {
                setConsentOpen(true);
              }}
            >
              {t('privacySettingsLink')}
            </button>
          </nav>
        </div>
      </div>

      {consentOpen && (
        <ConsentBanner
          t={t}
          onAnswered={() => {
            setConsentOpen(false);
          }}
        />
      )}
    </main>
  );
}
